// src/BikeInfo.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import useDebounce from './hooks/useDebounce';
import dataCache from './services/dataCache';

const CITIES = [
  { code: 'Taipei', name: '台北市' },
  { code: 'NewTaipei', name: '新北市' },
  { code: 'Taoyuan', name: '桃園市' },
  { code: 'Taichung', name: '台中市' },
  { code: 'Tainan', name: '台南市' },
  { code: 'Kaohsiung', name: '高雄市' },
  { code: 'Hsinchu', name: '新竹市' },
  { code: 'HsinchuCounty', name: '新竹縣' },
  { code: 'MiaoliCounty', name: '苗栗縣' },
  { code: 'PingtungCounty', name: '屏東縣' },
];

const BikeInfo = () => {
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bikeStations, setBikeStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // 獲取城市的自行車站點資料
  useEffect(() => {
    if (!selectedCity) {
      setBikeStations([]);
      setFilteredStations([]);
      return;
    }

    const fetchBikeData = async () => {
      setLoading(true);
      setError(null);
      
      const cacheKey = `bike_${selectedCity}`;
      const fetchFunction = async () => {
        const response = await fetch(
          `https://tdx.transportdata.tw/api/basic/v2/Bike/Availability/${selectedCity}?$format=JSON`
        );
        if (!response.ok) {
          throw new Error('獲取自行車站點資料失敗');
        }
        return await response.json();
      };

      try {
        const data = await dataCache.getData(cacheKey, fetchFunction);
        setBikeStations(data || []);
        setFilteredStations(data || []);
      } catch (err) {
        setError(err.message);
        setBikeStations([]);
        setFilteredStations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBikeData();

    // 訂閱自動更新
    const cacheKey = `bike_${selectedCity}`;
    const unsubscribe = dataCache.subscribe(cacheKey, (updatedData) => {
      console.log('[BikeInfo] 收到自動更新資料');
      if (updatedData && Array.isArray(updatedData)) {
        setBikeStations(updatedData);
        // 如果有搜尋詞，重新過濾
        if (debouncedSearchTerm) {
          const filtered = updatedData.filter(station =>
            station.StationName?.Zh_tw?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            station.StationAddress?.Zh_tw?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
          );
          setFilteredStations(filtered);
        } else {
          setFilteredStations(updatedData);
        }
      }
    });

    return () => {
      unsubscribe();
      dataCache.stopAutoRefresh(cacheKey);
    };
  }, [selectedCity, debouncedSearchTerm]);

  // 根據搜尋詞過濾站點
  useEffect(() => {
    if (!debouncedSearchTerm) {
      setFilteredStations(bikeStations);
      return;
    }

    const filtered = bikeStations.filter(station =>
      station.StationName?.Zh_tw?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      station.StationAddress?.Zh_tw?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    setFilteredStations(filtered);
  }, [debouncedSearchTerm, bikeStations]);

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCitySelect = (city) => {
    setSelectedCity(city.code);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const getAvailabilityStatus = (available, total) => {
    if (total === 0) return 'unavailable';
    const ratio = available / total;
    if (ratio > 0.3) return 'good';
    if (ratio > 0.1) return 'low';
    return 'critical';
  };

  const selectedCityName = CITIES.find(c => c.code === selectedCity)?.name || '';

  return (
    <div className="bike-info">
      <div className="search-container" ref={dropdownRef}>
        <select
          value={selectedCity}
          onChange={(e) => handleCitySelect(CITIES.find(c => c.code === e.target.value))}
          className="city-select"
        >
          <option value="">請選擇城市</option>
          {CITIES.map(city => (
            <option key={city.code} value={city.code}>
              {city.name}
            </option>
          ))}
        </select>
        
        {selectedCity && (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="搜尋站點名稱或地址"
            className="station-search"
          />
        )}
      </div>

      {!selectedCity && (
        <div className="info-message">請選擇一個城市</div>
      )}

      {loading && <div className="loading">載入中...</div>}
      {error && <div className="error">{error}</div>}

      {selectedCity && filteredStations.length > 0 && (
        <div className="result">
          <h2>{selectedCityName} 公共自行車站點 ({filteredStations.length})</h2>
          <div className="info-grid">
            {filteredStations.slice(0, 50).map((station, index) => {
              const availableStatus = getAvailabilityStatus(
                station.AvailableRentBikes,
                station.AvailableRentBikes + station.AvailableReturnBikes
              );
              
              return (
                <div key={station.StationUID || index} className="info-item bike-station">
                  <div className="station-header">
                    <span className="station-name">{station.StationName?.Zh_tw || '未知站點'}</span>
                    <span className={`availability-badge ${availableStatus}`}>
                      {station.AvailableRentBikes || 0} 可借
                    </span>
                  </div>
                  <div className="station-details">
                    <div className="detail-row">
                      <span className="label">可還車位：</span>
                      <span className="value">{station.AvailableReturnBikes || 0}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">總車位數：</span>
                      <span className="value">
                        {(station.AvailableRentBikes || 0) + (station.AvailableReturnBikes || 0)}
                      </span>
                    </div>
                    <div className="detail-row address">
                      <span className="label">地址：</span>
                      <span className="value">{station.StationAddress?.Zh_tw || '無地址資訊'}</span>
                    </div>
                    {station.UpdateTime && (
                      <div className="detail-row update-time">
                        <span className="label">更新時間：</span>
                        <span className="value">
                          {new Date(station.UpdateTime).toLocaleString('zh-TW', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {filteredStations.length > 50 && (
            <div className="info-message">
              顯示前 50 個站點，共 {filteredStations.length} 個站點
            </div>
          )}
        </div>
      )}

      {selectedCity && filteredStations.length === 0 && !loading && (
        <div className="info-message">找不到符合的站點</div>
      )}
    </div>
  );
};

export default BikeInfo;
